import { Link } from 'react-router-dom';
import { formatSubmittedAt, statusClass, STATUS_LABEL } from '../../lib/accountCreationUi';
import { formatDisplayPersonName } from '../../lib/formatPersonName';
import { splitPufItemCodeAndDescription } from '../../lib/pricingUpdateSubmissions';
import type { ApplicationSubmission, PricingUpdateFormData } from '../../types/applicationForm';

type Props = {
  row: ApplicationSubmission;
  detailPath: string;
};

function personLabel(raw: string | null | undefined): string {
  return formatDisplayPersonName(raw) || String(raw ?? '').trim();
}

function PersonStamp({
  name,
  at,
}: {
  name: string | null | undefined;
  at: string | null | undefined;
}) {
  const label = personLabel(name);
  if (!label && !at) return <span className="puf-list-row__muted">—</span>;
  return (
    <span className="puf-list-row__person">
      <span className="puf-list-row__person-name">{label || '—'}</span>
      {at ? <span className="puf-list-row__person-time">{formatSubmittedAt(at)}</span> : null}
    </span>
  );
}

export function PricingUpdateListRow({ row, detailPath }: Props) {
  const data = row.form_data as PricingUpdateFormData;
  const customerNo = data?.customer_no?.trim() || '';
  const customerName = (row.company_name || data?.customer_name || '').trim();
  const items = (data?.items ?? []).filter((item) => item.item_no.trim());

  return (
    <Link to={detailPath} className="puf-list-row">
      <span className="puf-list-row__ref-block">
        <span className="puf-list-row__ref">{row.application_no}</span>
        <span className={statusClass(row.status)}>{STATUS_LABEL[row.status]}</span>
      </span>

      <span className="puf-list-row__customer">
        {customerNo ? <span className="puf-list-row__customer-code">{customerNo}</span> : null}
        <span className="puf-list-row__customer-name">
          {customerName || (customerNo ? '' : '—')}
        </span>
      </span>

      <span className="puf-list-row__items">
        <span className="puf-list-row__items-head" aria-hidden>
          <span>Item</span>
          <span>UOM</span>
          <span>Price</span>
          <span>MOQ</span>
        </span>
        {items.length === 0 ? (
          <span className="puf-list-row__items-empty">No items</span>
        ) : (
          items.map((item) => {
            const split = splitPufItemCodeAndDescription(item.item_no, item.item_description);
            return (
              <span key={item.id} className="puf-list-row__item-line">
                <span className="puf-list-row__item-cell">
                  <span className="puf-list-row__item-code">{split.item_no || '—'}</span>
                  {split.item_description ? (
                    <span className="puf-list-row__item-desc">{split.item_description}</span>
                  ) : null}
                </span>
                <span className="puf-list-row__item-uom">{item.sales_uom.trim() || '—'}</span>
                <span className="puf-list-row__item-num">{item.sales_price.trim() || '—'}</span>
                <span className="puf-list-row__item-num">{item.moq.trim() || '—'}</span>
              </span>
            );
          })
        )}
      </span>

      <PersonStamp name={row.submitted_by || row.submitter_email} at={row.submitted_at} />
      <PersonStamp name={row.authorised_by} at={row.authorised_at} />
      <PersonStamp name={row.approved_by} at={row.approved_at} />
    </Link>
  );
}
